// src/ui/Hud.ts
//
// Phase 5 — top HUD strip (~40px) showing ante/round, money, and score/target.
// Static visual only; RoundScene calls setHudState() to update text.

import Phaser from 'phaser';
import { addCrispText } from './textHelper';

export interface HudStateOpts {
  ante: number;
  roundLabel: string;
  money: number;
  score: number;
  target: number;
  spin: number;
  maxSpins: number;
}

export class Hud extends Phaser.GameObjects.Container {
  private anteText!: Phaser.GameObjects.Text;
  private moneyText!: Phaser.GameObjects.Text;
  private scoreText!: Phaser.GameObjects.Text;
  private spinText!: Phaser.GameObjects.Text;
  // Phase 13 (UI-18): VIEW DECK pill button — opens the read-only numbers-deck
  // modal. Positioned between score (left) and money (right) per 13-UI-SPEC
  // §"Layout Contract": small pill inside HUD strip at y=6, x=220 (72×28px).
  private viewDeckBtn!: Phaser.GameObjects.Rectangle;
  private viewDeckLbl!: Phaser.GameObjects.Text;
  private onViewDeckTap: (() => void) | null = null;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);
    scene.add.existing(this);
    this.create();
  }

  private create(): void {
    const bg = this.scene.add
      .rectangle(0, 0, 375, 40, 0x070e16) // Phase 17: darker navy recedes behind gold text
      .setOrigin(0, 0);
    this.anteText = addCrispText(this.scene, 8, 4, 'Ante 1 · Small', {
      color: '#ffffff',
      fontSize: '14px',
    });
    this.moneyText = addCrispText(this.scene, 375 - 8, 4, '$4', {
      color: '#d4af37', // Phase 17: Art Deco gold
      fontSize: '14px',
    }).setOrigin(1, 0);
    this.scoreText = addCrispText(this.scene, 8, 22, 'Score 0 / 300', {
      color: '#cccccc',
      fontSize: '12px',
    });
    this.spinText = addCrispText(this.scene, 375 - 8, 22, 'Spin 0/3', {
      color: '#888888',
      fontSize: '12px',
    }).setOrigin(1, 0);
    // Phase 13 (UI-18): VIEW DECK pill — gold border, 11px gold label.
    // x=220 centers the pill (220..292) between score text (left) and money
    // text (right-anchored at ~x=367). The pill ends at x=292, leaving
    // ~75px clear before the money text — no overlap even at high money values.
    this.viewDeckBtn = this.scene.add
      .rectangle(220, 6, 72, 28, 0x064020)
      .setOrigin(0, 0)
      .setStrokeStyle(1, 0xd4af37) // Phase 17: Art Deco gold
      .setInteractive({ useHandCursor: true });
    this.viewDeckLbl = addCrispText(this.scene, 220 + 36, 6 + 14, 'VIEW DECK', {
      color: '#d4af37', // Phase 17: Art Deco gold
      fontSize: '11px',
    }).setOrigin(0.5);
    this.viewDeckBtn.on('pointerdown', () => {
      this.onViewDeckTap?.();
    });
    this.add([
      bg,
      this.anteText,
      this.moneyText,
      this.scoreText,
      this.spinText,
      this.viewDeckBtn,
      this.viewDeckLbl,
    ]);
  }

  /** Phase 13 (UI-18): register the VIEW DECK button tap handler. */
  setOnViewDeckTap(cb: () => void): void {
    this.onViewDeckTap = cb;
  }

  /** Update HUD from current run state. */
  setHudState(opts: HudStateOpts): void {
    this.anteText.setText(`Ante ${opts.ante} · ${opts.roundLabel}`);
    this.moneyText.setText(`$${opts.money}`);
    this.scoreText.setText(`Score ${opts.score} / ${opts.target}`);
    this.spinText.setText(`Spin ${opts.spin}/${opts.maxSpins}`);
  }
}
